/**
 * SOLOMON ORB — Color Palette
 *
 * Single source of truth for orb colors across the Three.js/WebGL pipeline.
 * CSS tokens don't reach WebGL — materials, buffer geometries, and GLSL
 * shader uniforms all read from this palette and rebuild on theme change.
 *
 * Dark mode values are byte-identical to prior hardcoded literals in
 * SolomonOrb/index.jsx, OrbMesh.jsx, and shaders.js. Any visual change in
 * dark mode is a regression.
 *
 * Light mode uses a MATERIAL model — particles are absorptive/pigmented
 * (darker than background) with warm highlights catching "light." Inverts
 * the luminosity ramp of dark mode. Previously: light particles glowing
 * against dark surround. Now: dark particles with warm highlights on
 * cream. Revised 2026-04-23 after external critique flagged the v1 light
 * palette as "inverted dark mode, not its own design."
 */

export const ORB_PALETTE = {
  dark: {
    // ── Scene point lights (three.js <pointLight color>) ──
    pointLightPrimary:   '#FF6B00',   // primary fire key light
    pointLightSecondary: '#C9A84C',   // gold fill
    pointListening:      '#00AAFF',   // listening override (theme-independent)
    pointListeningFill:  '#0088CC',

    // ── Particle bands (RGB floats written to BufferGeometry color attribute) ──
    // lerp(min, max, Math.random()) per channel — preserves organic warmth variation
    particleFire:      { rMin: 1.00, rMax: 1.00, gMin: 0.38, gMax: 0.66, bMin: 0.00, bMax: 0.05 },
    particleGold:      { rMin: 0.82, rMax: 0.82, gMin: 0.67, gMax: 0.67, bMin: 0.28, bMax: 0.43 },
    particleWarmWhite: { rMin: 1.00, rMax: 1.00, gMin: 0.93, gMax: 0.93, bMin: 0.72, bMax: 0.90 },

    // ── Connection lines (THREE.LineBasicMaterial color + useFrame opacity) ──
    connectionColor:       0xC9A84C,
    connectionAlphaIdle:   0.10,
    connectionAlphaSpeakBase: 0.20, connectionAlphaSpeakAmp: 0.12,
    connectionAlphaThinkBase: 0.20, connectionAlphaThinkAmp: 0.10,

    // ── Orbit stream (AccentStream THREE.PointsMaterial color) ──
    orbitStreamColor: 0xFF8533,

    // ── Error window (LatticeCore, 3000ms on the rising edge of isError) ──
    // = --status-error. Material colour multiplies the vertex colours, so the
    // gold lattice reads as a dimmed ember, not a flat red.
    errorTint: '#C43D3D',

    // ── GLSL fragment shader uniforms (vec3 triples for halo + future shaders) ──
    shaderOrange:     [1.000, 0.420, 0.000],   // #FF6B00
    shaderOrangeDeep: [0.800, 0.330, 0.000],   // #CC5400
    shaderAmber:      [0.831, 0.529, 0.039],   // #D4870A
    shaderGold:       [0.788, 0.659, 0.298],   // #C9A84C
    shaderGoldLight:  [0.910, 0.773, 0.416],   // #E8C56A
    shaderWarmWhite:  [1.000, 0.960, 0.870],
  },

  light: {
    // ── Scene point lights — deep burnt + carrot (material/absorptive model) ──
    pointLightPrimary:   '#6E3010',
    pointLightSecondary: '#B45420',
    pointListening:      '#00AAFF',   // blue listening state independent of theme
    pointListeningFill:  '#0088CC',

    // ── Particle bands — darker-than-background pigments with warm highlights ──
    // Fire band "deep core":        #6E3010 (0.420, 0.188, 0.063) — 45% of particles
    // Gold band "warm highlight":   #B45420 (0.706, 0.329, 0.125) — 33%
    // Warm-white band "soft amber": #D89650 (0.847, 0.588, 0.314) — 22%
    //   (pure white would be invisible against cream — changed from dark-mode model)
    // Narrow per-channel variance preserves organic gradient without drifting far from base
    particleFire:      { rMin: 0.40, rMax: 0.45, gMin: 0.18, gMax: 0.22, bMin: 0.05, bMax: 0.08 },
    particleGold:      { rMin: 0.68, rMax: 0.74, gMin: 0.31, gMax: 0.36, bMin: 0.10, bMax: 0.16 },
    particleWarmWhite: { rMin: 0.82, rMax: 0.88, gMin: 0.56, gMax: 0.62, bMin: 0.28, bMax: 0.35 },

    // ── Connection lines — deep burnt, idle bumped to 0.45 for material-model legibility ──
    connectionColor:       0x6E3010,
    connectionAlphaIdle:   0.45,
    connectionAlphaSpeakBase: 0.60, connectionAlphaSpeakAmp: 0.15,
    connectionAlphaThinkBase: 0.55, connectionAlphaThinkAmp: 0.15,

    // ── Orbit stream ──
    orbitStreamColor: 0x6E3010,

    // ── Error window — the light theme's --status-error (5.74:1 on cream) ──
    errorTint: '#A83232',

    // ── GLSL shader uniforms (material model: highlights catch warm light) ──
    shaderOrange:     [0.706, 0.329, 0.125],   // #B45420 (warm highlight, NOT dominant)
    shaderOrangeDeep: [0.239, 0.094, 0.020],   // #3D1805 (very deep burnt)
    shaderAmber:      [0.847, 0.588, 0.314],   // #D89650 (soft amber highlight)
    shaderGold:       [0.706, 0.329, 0.125],   // #B45420 (gold collapses to carrot)
    shaderGoldLight:  [0.816, 0.429, 0.189],   // derived lighter from #B45420
    shaderWarmWhite:  [0.847, 0.588, 0.314],   // #D89650 — pure white invisible on cream
  },
};

/**
 * Resolve palette for a given theme string. Falls back to dark on unknown.
 */
export function getOrbPalette(theme) {
  return ORB_PALETTE[theme] || ORB_PALETTE.dark;
}
